/**
 * Provider breakdown component showing payment distribution by provider.
 */

import { getCurrencySymbol } from "../../../../lib/format.ts";

interface ProviderStats {
  provider: string;
  count: number;
  amount: string;
  currency: string;
  successRate: number;
}

interface ProviderBreakdownProps {
  data: ProviderStats[];
  totalAmount: number;
}

const PROVIDER_LABELS: Record<string, string> = {
  MANUAL: "Переводы",
  STRIPE: "Stripe",
  YOOMONEY: "YooMoney",
  EXGO: "ExGo",
};

const PROVIDER_COLORS: Record<string, string> = {
  MANUAL: "bg-blue-500",
  STRIPE: "bg-purple-500",
  YOOMONEY: "bg-green-500",
  EXGO: "bg-orange-500",
};

export function ProviderBreakdown({ data, totalAmount }: ProviderBreakdownProps) {
  if (data.length === 0) {
    return (
      <div className="card p-4">
        <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
          По провайдерам
        </p>
        <p className="text-[13px] text-slate-500 text-center py-4">
          Нет данных
        </p>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
        По провайдерам
      </p>
      <div className="space-y-3">
        {data.map((item) => {
          const amount = parseFloat(item.amount);
          const percentage = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;
          const barColor = PROVIDER_COLORS[item.provider] || "bg-slate-400";
          const label = PROVIDER_LABELS[item.provider] || item.provider;

          return (
            <div key={item.provider}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[13px] font-medium text-slate-700">
                  {label}
                </span>
                <span className="text-[13px] text-slate-600">
                  {amount.toLocaleString()} {getCurrencySymbol(item.currency)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${barColor} rounded-full transition-all duration-300`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
                <span className="text-[11px] text-slate-500 w-10 text-right">
                  {percentage.toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-slate-400">
                  {item.count} заказов
                </span>
                <span className="text-[11px] text-slate-400">
                  Успех: {(item.successRate * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
